import React, { useState, useEffect } from 'react';
import get from 'lodash/get';
import axios from 'axios';

import DeletePartner from './deletePartner';
import { SdfBox, SdfButton, SdfSelect, SdfShimmer } from '@synerg/react-components';
import Toast from '../common/toast';
import { uniqueId } from 'lodash';

export default function PartnerDetailsAndAssignContact({ setDeletedPartner, partner, roles = [], onClose, updateContact, hasUpdatedContacts, setHasUpdatedContacts, setPartner }) {
	const {
		orgName = '',
		ooid = '',
	} = partner;
	const technicalContact = get(partner, 'technicalContacts.displayName');
	const businessContact = get(partner, 'businessContacts.displayName');
	const partnerContact = get(partner, 'partnerContacts.displayName');
	const [loadingPartnerDetails, setLoadingPartnerDetails] = useState(null);
	const [partnerDetails, setPartnerDetails] = useState(null);
	const [busy, setBusy] = useState(false);
	const [technicalContactName, setTechnicalContactName] = useState(technicalContact);
	const [businessName, setBusinessName] = useState(businessContact);
	const [partnerName, setPartnerName] = useState(partnerContact);
	const [contacts, setContacts] = useState([]);
	const [technicalContacts, setTechnicalContacts] = useState([]);
	const [businessContacts, setBusinessContacts] = useState([]);
	const [partnerContacts, setPartnerContacts] = useState([]);
	const [openDeletePartner, setOpenDeletePartner] = useState(false);
	const [toast, setToast] = useState(false);

	const [allTechnicalContacts, setAllTechnicalContacts] = useState([]);
	const [allBusinessContacts, setAllBusinessContacts] = useState([]);
	const [allPartnerContacts, setAllPartnerContacts] = useState([]);

	const getContacts = type => contacts
		.filter(({ position }) => position === type);

	const handleResponse = (message, status = "error") => {
		setToast({
			key: uniqueId(),
			status: status,
			message: message,
			showToast: true
		});
	};

	const assignAdvisors = (assignedAdvisor, advisorName) => {
		if (assignedAdvisor.includes("technicalContacts")) {
			setPartner((prevState) => {
				return {
					...prevState,
					technicalContacts:
					{
						displayName: advisorName
					}
				}
			});
		}
		if (assignedAdvisor.includes("businessContacts")) {
			setPartner((prevState) => {
				return {
					...prevState,
					businessContacts:
					{
						displayName: advisorName
					}
				}
			});
		}
		if (assignedAdvisor.includes("partnerContacts")) {
			setPartner((prevState) => {
				return {
					...prevState,
					partnerContacts:
					{
						displayName: advisorName
					}
				}
			});
		}
	}

	const hideDeletePartnerModal = () => {
		// this function is only when no deletion
		// has occurred
		setOpenDeletePartner(false);
	};

	const closeDeletePartnerModal = () => {
		if (onClose) {
			onClose();
		}
		setOpenDeletePartner(false);
	}

	const assignContacts = async () => {
		const payload = {};
		const assignedTechnical = technicalContactName && getContacts('technical').find(({ firstName, lastName }) => `${firstName} ${lastName}` === technicalContactName);
		const assignedBusinessDev = businessName && getContacts('business').find(({ firstName, lastName }) => `${firstName} ${lastName}` === businessName);
		const assignedPartnerDev = partnerName && getContacts('partner').find(({ firstName, lastName }) => `${firstName} ${lastName}` === partnerName);
		if (assignedTechnical) {
			payload.technical = assignedTechnical._id;
		}
		if (assignedBusinessDev) {
			payload.business = assignedBusinessDev._id;
		}
		if (assignedPartnerDev) {
			payload.partner = assignedPartnerDev._id;
		}
		if (assignedTechnical || technicalContactName.includes("Not assigned")) {
			assignAdvisors("technicalContacts", technicalContactName);
		}
		if (assignedBusinessDev || businessName.includes("Not assigned")) {
			assignAdvisors("businessContacts", businessName);
		}
		if (assignedPartnerDev || partnerName.includes("Not assigned")) {
			assignAdvisors("partnerContacts", partnerName);
		}
		setHasUpdatedContacts(false);
		setBusy(true);
		try {
			await axios.post(
				`${import.meta.env.VITE_ROOT_PATH}/api/partners/${ooid}`,
				payload
			);
			handleResponse("Successfully updated partner contacts", "success");
			setHasUpdatedContacts(true);
		} catch (e) {
			const errorMessage = get(e, 'response.data.message', 'Something went wrong when trying to get contacts. Please try again.');
			handleResponse(errorMessage);
		}
		setBusy(false);
	};

	const fetchPartnerDetails = async () => {
		try {
			setLoadingPartnerDetails(true);
			const { data } = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/partner/details/${ooid}`);
			const partnerInfo = { ...data, ...data.address };
			if (partnerInfo.createdDate) {
				partnerInfo.createdDate = new Date(partnerInfo.createdDate).toLocaleString('en-US', {
					month: 'long',
					day: 'numeric',
					year: 'numeric',
				});
			}
			setPartnerDetails(partnerInfo);
			setLoadingPartnerDetails(false);
		} catch (error) {
			setLoadingPartnerDetails(false);
			const errorMessage = get(error, 'response.data.message', 'Something went wrong when trying to get partner details. Please try again.');
			handleResponse(errorMessage);
		}
	};

	const getContactData = async () => {
		try {
			const { data: contactData } = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/partner/contacts`);
			setContacts(contactData);
		} catch (e) {
			const errorMessage = get(e, 'response.data.message', 'Something went wrong when trying to get contacts. Please try again.');
			handleResponse(errorMessage);
		}
	};

	useEffect(() => {
		if (!partnerDetails) {
			fetchPartnerDetails();
			getContactData()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (updateContact) {
			console.log('updateContact', updateContact);
			assignContacts();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updateContact]);

	useEffect(() => {
		const getContactNames = type => getContacts(type)
			.map(({ firstName, lastName }) => ({ label: `${firstName} ${lastName}`, value: `${firstName} ${lastName}` }));
		setAllTechnicalContacts(getContactNames('technical'));
		setAllBusinessContacts(getContactNames('business'));
		setAllPartnerContacts(getContactNames('partner'));
		setTechnicalContacts(getContactNames('technical'));
		setBusinessContacts(getContactNames('business'));
		setPartnerContacts(getContactNames('partner'));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contacts])

	const handleFilter = (e, type) => {
		switch (type) {
			case "business":
				setBusinessContacts(
					allBusinessContacts.filter(partner =>
						partner.value.toLowerCase().includes(e.detail.toLowerCase())
					)
				);
				break;
			case "partner":
				setPartnerContacts(
					allPartnerContacts.filter(partner =>
						partner.value.toLowerCase().includes(e.detail.toLowerCase())
					)
				);
				break;
			default:
				setTechnicalContacts(
					allTechnicalContacts.filter(partner =>
						partner.value.toLowerCase().includes(e.detail.toLowerCase())
					)
				);
				break;
		}
	};

	const getAddressDetails = () => {
		const parts = [];
		if (get(partnerDetails, 'city', '')) {
			parts.push(get(partnerDetails, 'city', '') + ',');
			parts.push(get(partnerDetails, 'stateCode', ''));
			parts.push(get(partnerDetails, 'postalCode', ''));
		} else {
			parts.push(get(partnerDetails, 'stateCode', ''));
			parts.push(get(partnerDetails, 'postalCode', ''));
		}
		return parts.join(' ').replace(/\s+/g, ' ');
	};

	useEffect(() => {
		if (hasUpdatedContacts) {
			console.log('hasUpdatedContacts', hasUpdatedContacts);
		}
	}, [hasUpdatedContacts])

	return (
		<div className="mx-5">
			{toast && <Toast
				key={toast.key}
				status={toast.status}
				message={toast.message}
				showToast={toast.showToast}
			/>}
			<div className="manage-partner-slidein">
				<div className="my-1 flex flex-row sm:w-full justify-between">
					<div className="text-heading-06 font-black">{partner.orgName || ''}</div>
					<div>
						{roles.includes('selfservicesuperuser') && <div>
							<SdfButton
								emphasis="primary"
								action="destructive"
								variant="contained"
								onClick={() => setOpenDeletePartner(true)}
								{...(loadingPartnerDetails && { disabled: true })}
							>
								Delete
							</SdfButton>
							{openDeletePartner && <DeletePartner
								hideDeletePartnerModal={hideDeletePartnerModal}
								setDeletedPartner={setDeletedPartner}
								orgName={orgName}
								ooid={ooid}
								closeDeletePartnerModal={closeDeletePartnerModal}
							/>}
						</div>}
					</div>
				</div>

				<div className="mt-1 text-body-02">
					<div>{loadingPartnerDetails ? <SdfShimmer className="block w-xs" /> : get(partnerDetails, 'main', '')}</div>
					<div>{get(partnerDetails, 'optional1', '')}</div>
					<div>{loadingPartnerDetails ? <SdfShimmer className="block w-xs" /> : getAddressDetails()}</div>
				</div>
				<div className="mini-table mt-5 mb-2">
					<div className="flex flex-row justify-between mt-4">
						<div className="flex-col">
							<div className="order-1">
								<span className="text-heading-04 font-bold sm:text-default">OOID</span>
							</div>
							<div className="order-2">
								<span className="text-body-01 ">{partner.ooid || ''}</span>
							</div>
						</div>

						<div className="flex-col">
							<div className="order-1">
								<span className="text-heading-04 font-bold sm:text-default">Partner ID</span>
							</div>
							<div className="order-2">
								<span className="text-body-01 ">{loadingPartnerDetails ? <SdfShimmer /> : get(partnerDetails, 'isiOrgCd', 'N/A')}</span>
							</div>
						</div>
						<div className="flex-col">
							<div className="order-1">
								<span className="text-heading-04 font-bold sm:text-default">Partner type</span>
							</div>
							<div className="order-2">
								<span className="text-body-01 capitalize">
										{loadingPartnerDetails ? <SdfShimmer /> : 
												// Show 'B2C' if fdxSupport is true, otherwise show partnerType
												(get(partnerDetails, 'fdxSupport') ? 'B2C' : get(partnerDetails, 'partnerType', 'N/A'))
										}
								</span>
							</div>
						</div>

						<div className="flex-col">
							<div className="order-1">
								<span className="text-heading-04 font-bold sm:text-default">Created on</span>
							</div>
							<div className="order-2">
								<span className="text-body-01 capitalize">{loadingPartnerDetails ? <SdfShimmer /> : get(partnerDetails, 'createdDate', 'N/A')}</span>
							</div>
						</div>
						<div className="flex-col">
							<div className="order-1">
								<span className="text-heading-04 font-bold sm:text-default">Created By</span>
							</div>
							<div className="order-2">
								<span className="text-body-01 capitalize">{loadingPartnerDetails ? <SdfShimmer /> : get(partnerDetails, 'createdBy', 'Not created in Developer Self-Service')}</span>
							</div>
						</div>
					</div>
				</div>

				<SdfBox variant="filled" className="my-5 p-5">
					<div className='flex justify-between'>
						<div className="textbox-with-label">
							<SdfSelect
								label={"Technical advisor"}
								className=""
								filterable={true}
								items={technicalContacts}
								value={{ label: technicalContactName, value: technicalContactName }}
								placeholder="Select..."
								inputWidth={"lg"}
								onSdfChange={({ detail }) => { setTechnicalContactName(detail.value); setTechnicalContacts(allTechnicalContacts); }}
								onSdfFilter={(e) => { handleFilter(e, 'technical') }}
							/>
						</div>
						<div className="textbox-with-label">
							<SdfSelect
								label={"Business development adv"}
								className=""
								filterable={true}
								items={businessContacts}
								value={{ label: businessName, value: businessName }}
								placeholder="Select..."
								inputWidth={"lg"}
								onSdfChange={({ detail }) => { setBusinessName(detail.value); setBusinessContacts(allBusinessContacts); }}
								onSdfFilter={(e) => { handleFilter(e, 'business') }}
							/>
						</div>
						<div className="textbox-with-label">
							<SdfSelect
								label={"Partner development adv"}
								className=""
								filterable={true}
								items={partnerContacts}
								value={{ label: partnerName, value: partnerName }}
								placeholder="Select..."
								inputWidth={"lg"}
								onSdfChange={({ detail }) => { setPartnerName(detail.value); setPartnerContacts(allPartnerContacts); }}
								onSdfFilter={(e) => { handleFilter(e, 'partner') }}
							/>
						</div>
						<SdfButton {...(busy && { disabled: true })} onClick={assignContacts} emphasis="tertiary" busy={busy} > Save</SdfButton>
					</div>
				</SdfBox>
			</div>
		</div>
	);
}
